import { useState, useEffect, useCallback } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown, Search } from "lucide-react";

/**
 * Reusable DataTable with server-side OR client-side pagination, sorting, and search.
 *
 * Server-side mode (default):
 *   fetchData: async ({ page, pageSize, sort, order, search }) => { items, total, page, page_size, total_pages }
 *
 * Client-side mode (for data already in memory):
 *   data: array of objects — overrides fetchData
 *   searchFields: string[] — keys to match for client-side search
 *
 * Common props:
 *   columns: [{ key, label, sortable?, render?, className?, cellClassName? }]
 *   defaultSort, defaultOrder, defaultPageSize, searchPlaceholder
 *   onRowClick, refreshKey, rowClassName, emptyMessage
 */

function clientPaginate(allRows, { page, pageSize, sort, order, search, searchFields }) {
  let rows = [...allRows];
  if (search && searchFields?.length) {
    const s = search.toLowerCase();
    rows = rows.filter(r => searchFields.some(f => String(r[f] ?? "").toLowerCase().includes(s)));
  }
  if (sort) {
    const rev = (order || "desc") !== "asc";
    rows.sort((a, b) => {
      const va = a[sort], vb = b[sort];
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === "number" && typeof vb === "number") return rev ? vb - va : va - vb;
      return rev ? String(vb).localeCompare(String(va)) : String(va).localeCompare(String(vb));
    });
  }
  const total = rows.length;
  const p = Math.max(1, page);
  const ps = Math.max(1, Math.min(pageSize, 500));
  const start = (p - 1) * ps;
  return { items: rows.slice(start, start + ps), total, page: p, page_size: ps, total_pages: Math.ceil(total / ps) || 0 };
}

export default function DataTable({
  fetchData,
  data,
  searchFields,
  columns = [],
  defaultSort = "",
  defaultOrder = "desc",
  defaultPageSize = 50,
  searchPlaceholder = "Search...",
  onRowClick,
  refreshKey,
  rowClassName,
  emptyMessage = "No data found",
}) {
  const isClientMode = Array.isArray(data);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sort, setSort] = useState(defaultSort);
  const [order, setOrder] = useState(defaultOrder);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (isClientMode) {
      const res = clientPaginate(data, { page, pageSize, sort, order, search, searchFields });
      setItems(res.items); setTotal(res.total); setTotalPages(res.total_pages);
      return;
    }
    if (!fetchData) return;
    setLoading(true);
    try {
      const res = await fetchData({ page, pageSize, sort, order, search });
      setItems(res.items || []);
      setTotal(res.total || 0);
      setTotalPages(res.total_pages || 0);
    } catch (e) {
      console.error("DataTable fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [isClientMode, data, fetchData, page, pageSize, sort, order, search, searchFields]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleSort = (key) => {
    if (sort === key) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setOrder("desc");
    }
    setPage(1);
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    setSearch(searchInput);
    setPage(1);
  };

  const handlePageSize = (val) => {
    setPageSize(Number(val));
    setPage(1);
  };

  const startRow = (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, total);

  return (
    <div className="space-y-3">
      {/* Top controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 h-9 w-64"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-9">
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-9"
              onClick={() => { setSearchInput(""); setSearch(""); setPage(1); }}
            >
              Clear
            </Button>
          )}
        </form>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>Rows per page</span>
          <Select value={String(pageSize)} onValueChange={handlePageSize}>
            <SelectTrigger className="h-8 w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`py-2.5 px-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap ${
                    col.sortable !== false ? "cursor-pointer select-none hover:text-foreground" : ""
                  } ${col.className || ""}`}
                  onClick={() => col.sortable !== false && handleSort(col.key)}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.sortable !== false && (
                      sort === col.key ? (
                        order === "asc" ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />
                      ) : (
                        <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />
                      )
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                  <div className="animate-spin inline-block rounded-full h-6 w-6 border-b-2 border-primary mb-2" />
                  <p>Loading...</p>
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              items.map((row, i) => (
                <tr
                  key={row.id || row._id || i}
                  className={`border-b last:border-0 transition-colors ${
                    onRowClick ? "cursor-pointer hover:bg-muted/50" : ""
                  } ${rowClassName ? rowClassName(row) : ""} ${loading ? "opacity-60" : ""}`}
                  onClick={() => onRowClick?.(row)}
                >
                  {columns.map((col) => (
                    <td key={col.key} className={`py-2.5 px-3 ${col.cellClassName || ""}`}>
                      {col.render ? col.render(row, i) : row[col.key] ?? ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Bottom pagination */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <span className="text-muted-foreground">
          {total > 0 ? `Showing ${startRow}-${endRow} of ${total}` : "No results"}
          {search && <span className="ml-1"> for &quot;{search}&quot;</span>}
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(1)}>
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="px-3 text-muted-foreground">
            Page {page} of {totalPages || 1}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(totalPages)}>
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
